import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isValidatingSession, setIsValidatingSession] = useState(true);
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  // Simple storage manager
  const storageManager = {
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.warn('Storage failed:', error);
      }
    },
    
    getItem: (key) => {
      try {
        return localStorage.getItem(key) || sessionStorage.getItem(key);
      } catch (error) {
        console.warn('Storage read failed:', error);
        return null;
      }
    },
    
    removeItem: (key) => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('Storage remove failed:', error);
      }
    }
  };

  // Simple session validation
  const validateSession = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://teenagefashion.in/backend/api' 
          : 'http://localhost/newfashion/backend/api');
      
      const response = await fetch(`${API_URL}/validate_session.php`, {
        credentials: 'include',
        method: 'GET'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsAuthenticated(true);
          setUser(data.user);
          const token = storageManager.getItem('authToken');
          if (token) {
            setAuthToken(token);
          }
          return true;
        }
      }
      
      // Check localStorage token as fallback
      const storedToken = storageManager.getItem('authToken');
      if (storedToken) {
        setIsAuthenticated(true);
        setUser({ email: 'admin@fashion.com', role: 'admin' });
        setAuthToken(storedToken);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://teenagefashion.in/backend/api' 
          : 'http://localhost/newfashion/backend/api');
      
      const response = await fetch(`${API_URL}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const token = data.token || '1';
        const userData = { email, role: 'admin' };
        
        storageManager.setItem('authToken', token);
        
        setIsAuthenticated(true);
        setUser(userData);
        setAuthToken(token);
        
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Invalid credentials' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Network error' };
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' 
          ? 'https://teenagefashion.in/backend/api' 
          : 'http://localhost/newfashion/backend/api');
      
      await fetch(`${API_URL}/logout.php`, { 
        method: 'POST', 
        credentials: 'include' 
      });
    } catch (error) {
      console.warn('Logout API call failed:', error);
    }
    
    storageManager.removeItem('authToken');
    
    setIsAuthenticated(false);
    setUser(null);
    setAuthToken(null);
  }, []);

  // Initialize authentication on app start - only once
  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      setIsValidatingSession(true);
      const isValid = await validateSession();
      
      if (isMounted) {
        if (!isValid) {
          setIsAuthenticated(false);
          setUser(null);
          setAuthToken(null);
        }
        setIsValidatingSession(false);
      }
    };
    
    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once

  const value = {
    isAuthenticated,
    isValidatingSession,
    user,
    authToken,
    login,
    logout,
    validateSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
